import React, { useEffect, useState, useCallback, useMemo } from "react";
import { View, ActivityIndicator, StyleSheet, Pressable, TextInput } from "react-native";
import { PreviewSection } from "../shared/PreviewSection";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { userService, UserAccountInfo, PasswordRequirements } from "@/services/userService";
import useToast from "@/hooks/useToast";
import { useTheme } from "@/hooks/useTheme";
import { useModal } from "@/hooks/useModal";
import usePermissions from "@/hooks/usePermissions";
import { ConfirmModal } from "@/components/Modals/ConfirmModal";

const DEFAULT_PASSWORD_REQUIREMENTS: PasswordRequirements = {
  min_length: 8,
  require_uppercase: true,
  require_lowercase: true,
  require_number: true,
  require_special: true,
};

interface UserAccountSectionProps {
  userId: string;
  accessToken: string;
  editing?: boolean;
}

function formatDate(iso: string | null): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function PasswordRequirementRow({ met, text, colors }: { met: boolean; text: string; colors: any }) {
  return (
    <View style={styles.requirementRow}>
      <MaterialIcons
        name={met ? "check-circle" : "radio-button-unchecked"}
        size={16}
        color={met ? colors.emerald?.text || colors.primary : colors.textSecondary}
      />
      <ThemedText style={{ fontSize: 12, color: met ? colors.text : colors.textSecondary }}>{text}</ThemedText>
    </View>
  );
}

export function UserAccountSection({ userId, accessToken, editing = false }: UserAccountSectionProps) {
  const [account, setAccount] = useState<UserAccountInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [confirmingEmail, setConfirmingEmail] = useState(false);
  const toast = useToast();
  const { colors } = useTheme();
  const { openModal, closeModal } = useModal();
  const { users: usersPermissions } = usePermissions();
  const canUpdate = usersPermissions.update;

  const tenantRequirements = account?.password_requirements ?? DEFAULT_PASSWORD_REQUIREMENTS;

  const passwordChecks = useMemo(
    () => ({
      minLength: password.length >= tenantRequirements.min_length,
      hasUppercase: !tenantRequirements.require_uppercase || /[A-Z]/.test(password),
      hasLowercase: !tenantRequirements.require_lowercase || /[a-z]/.test(password),
      hasNumber: !tenantRequirements.require_number || /[0-9]/.test(password),
      hasSpecial: !tenantRequirements.require_special || /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }),
    [password, tenantRequirements],
  );

  const allRequirementsMet = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSavePassword = allRequirementsMet && passwordsMatch && !savingPassword;

  // Collapse password form when exiting edit mode
  useEffect(() => {
    if (!editing) {
      setShowPasswordForm(false);
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [editing]);

  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      try {
        setLoading(true);
        const info = await userService.getUserAccountInfo(userId, accessToken);
        if (!cancelled) setAccount(info);
      } catch (err: any) {
        console.error("Failed to fetch account info:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetch();
    return () => {
      cancelled = true;
    };
  }, [userId, accessToken]);

  const performResendInvite = useCallback(async () => {
    setResending(true);
    try {
      const msg = await userService.resendInvite(userId, accessToken);
      toast.showToast({ title: "Sent", message: msg, toastType: "success" });
    } catch (err: any) {
      toast.showToast({
        title: "Error",
        message: err.message || "Failed to resend invite",
        toastType: "error",
      });
    } finally {
      setResending(false);
    }
  }, [userId, accessToken, toast]);

  const performSendPasswordReset = useCallback(async () => {
    setResending(true);
    try {
      const msg = await userService.sendPasswordReset(userId, accessToken);
      toast.showToast({ title: "Sent", message: msg, toastType: "success" });
    } catch (err: any) {
      toast.showToast({
        title: "Error",
        message: err.message || "Failed to send password reset",
        toastType: "error",
      });
    } finally {
      setResending(false);
    }
  }, [userId, accessToken, toast]);

  const handleResendInvite = useCallback(() => {
    openModal(
      <ConfirmModal
        title="Resend Invite"
        message="This will send a new invitation email to the user. Continue?"
        confirmText="Send"
        confirmColor={colors.primary}
        onConfirm={() => {
          closeModal();
          performResendInvite();
        }}
        onCancel={closeModal}
      />,
    );
  }, [openModal, closeModal, performResendInvite, colors.primary]);

  const handleSendPasswordReset = useCallback(() => {
    openModal(
      <ConfirmModal
        title="Send Password Reset"
        message="This will send a password reset email to the user. Continue?"
        confirmText="Send"
        confirmColor={colors.primary}
        onConfirm={() => {
          closeModal();
          performSendPasswordReset();
        }}
        onCancel={closeModal}
      />,
    );
  }, [openModal, closeModal, performSendPasswordReset, colors.primary]);

  const performConfirmEmail = useCallback(async () => {
    setConfirmingEmail(true);
    try {
      const msg = await userService.confirmEmail(userId, accessToken);
      toast.showToast({ title: "Success", message: msg, toastType: "success" });
      setAccount((prev) => (prev ? { ...prev, email_confirmed_at: new Date().toISOString() } : prev));
    } catch (err: any) {
      toast.showToast({
        title: "Error",
        message: err.message || "Failed to confirm email",
        toastType: "error",
      });
    } finally {
      setConfirmingEmail(false);
    }
  }, [userId, accessToken, toast]);

  const performResendConfirmation = useCallback(async () => {
    setConfirmingEmail(true);
    try {
      const msg = await userService.resendConfirmation(userId, accessToken);
      toast.showToast({ title: "Sent", message: msg, toastType: "success" });
    } catch (err: any) {
      toast.showToast({
        title: "Error",
        message: err.message || "Failed to resend confirmation",
        toastType: "error",
      });
    } finally {
      setConfirmingEmail(false);
    }
  }, [userId, accessToken, toast]);

  const handleConfirmEmail = useCallback(() => {
    openModal(
      <ConfirmModal
        title="Confirm Email"
        message="This will force-confirm the user's email address without them clicking a link. Continue?"
        confirmText="Confirm"
        confirmColor={colors.primary}
        onConfirm={() => {
          closeModal();
          performConfirmEmail();
        }}
        onCancel={closeModal}
      />,
    );
  }, [openModal, closeModal, performConfirmEmail, colors.primary]);

  const handleResendConfirmation = useCallback(() => {
    openModal(
      <ConfirmModal
        title="Resend Confirmation"
        message="This will send a confirmation email to the user. Continue?"
        confirmText="Send"
        confirmColor={colors.primary}
        onConfirm={() => {
          closeModal();
          performResendConfirmation();
        }}
        onCancel={closeModal}
      />,
    );
  }, [openModal, closeModal, performResendConfirmation, colors.primary]);

  const handleSavePassword = useCallback(async () => {
    setSavingPassword(true);
    try {
      const msg = await userService.setPassword(userId, password, accessToken);
      toast.showToast({ title: "Success", message: msg, toastType: "success" });
      setShowPasswordForm(false);
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (err: any) {
      toast.showToast({
        title: "Error",
        message: err.message || "Failed to set password",
        toastType: "error",
      });
    } finally {
      setSavingPassword(false);
    }
  }, [userId, password, accessToken, toast]);

  if (loading) {
    return (
      <PreviewSection title="Account">
        <View style={{ padding: 16, alignItems: "center" }}>
          <ActivityIndicator size="small" color={colors.primary} />
        </View>
      </PreviewSection>
    );
  }

  if (!account) {
    return (
      <PreviewSection title="Account">
        <ThemedText style={{ opacity: 0.6 }}>Unable to load account info</ThemedText>
      </PreviewSection>
    );
  }

  const cellStyle = [styles.cell, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }];
  const pillDisabled = !editing;

  return (
    <PreviewSection title="Account">
      <View style={styles.row}>
        {/* ── SIGN-IN ── */}
        <View style={cellStyle}>
          <View style={styles.cellHeader}>
            <MaterialCommunityIcons name="login" size={13} color={colors.textSecondary} />
            <ThemedText style={[styles.cellHeaderText, { color: colors.textSecondary }]}>SIGN-IN</ThemedText>
          </View>
          <ThemedText style={styles.primaryText}>{account.has_signed_in ? "Yes" : "Not yet"}</ThemedText>
        </View>

        {/* ── LAST SIGN-IN ── */}
        <View style={cellStyle}>
          <View style={styles.cellHeader}>
            <MaterialCommunityIcons name="clock-outline" size={13} color={colors.textSecondary} />
            <ThemedText style={[styles.cellHeaderText, { color: colors.textSecondary }]}>LAST SIGN-IN</ThemedText>
          </View>
          <ThemedText style={styles.primaryText}>{formatDate(account.last_sign_in_at)}</ThemedText>
        </View>

        {/* ── PASSWORD ── */}
        <View style={cellStyle}>
          <View style={styles.cellHeader}>
            <MaterialCommunityIcons name="lock-outline" size={13} color={colors.textSecondary} />
            <ThemedText style={[styles.cellHeaderText, { color: colors.textSecondary }]}>PASSWORD</ThemedText>
            <View
              style={[
                styles.statusDot,
                { backgroundColor: account.needs_password_update ? colors.red.background : colors.emerald.background },
              ]}
            />
            <ThemedText
              style={[
                styles.statusText,
                { color: account.needs_password_update ? colors.red.text : colors.emerald.text },
              ]}
            >
              {account.needs_password_update ? "Expired" : "Current"}
            </ThemedText>
          </View>
          <ThemedText style={[styles.secondaryText, { color: colors.textSecondary }]}>
            {account.password_expires_at ? `Expires: ${formatDate(account.password_expires_at)}` : "No policy"}
          </ThemedText>
        </View>
      </View>

      {/* ── Action pills (only if user has update permission) ── */}
      {canUpdate && (
        <View style={[styles.pillRow, pillDisabled && styles.pillRowDisabled]}>
          {account.has_signed_in ? (
            <Pressable
              onPress={handleSendPasswordReset}
              disabled={pillDisabled || resending}
              style={({ hovered }) => [
                styles.pill,
                {
                  borderColor: colors.border,
                  backgroundColor: hovered && !pillDisabled ? colors.backgroundSecondary : "transparent",
                },
              ]}
            >
              <MaterialCommunityIcons name="lock-reset" size={14} color={colors.textSecondary} />
              <ThemedText style={[styles.pillText, { color: colors.text }]}>
                {resending ? "Sending..." : "Send Password Reset"}
              </ThemedText>
            </Pressable>
          ) : (
            <Pressable
              onPress={handleResendInvite}
              disabled={pillDisabled || resending}
              style={({ hovered }) => [
                styles.pill,
                {
                  borderColor: colors.border,
                  backgroundColor: hovered && !pillDisabled ? colors.backgroundSecondary : "transparent",
                },
              ]}
            >
              <MaterialCommunityIcons name="email-outline" size={14} color={colors.textSecondary} />
              <ThemedText style={[styles.pillText, { color: colors.text }]}>
                {resending ? "Sending..." : "Resend Invite"}
              </ThemedText>
            </Pressable>
          )}

          <Pressable
            onPress={() => setShowPasswordForm((prev) => !prev)}
            disabled={pillDisabled}
            style={({ hovered }) => [
              styles.pill,
              {
                borderColor: showPasswordForm ? colors.primary : colors.border,
                backgroundColor: showPasswordForm
                  ? colors.primary + "15"
                  : hovered && !pillDisabled
                    ? colors.backgroundSecondary
                    : "transparent",
              },
            ]}
          >
            <MaterialCommunityIcons
              name="lock-reset"
              size={14}
              color={showPasswordForm ? colors.primary : colors.textSecondary}
            />
            <ThemedText style={[styles.pillText, { color: showPasswordForm ? colors.primary : colors.text }]}>
              Set Password
            </ThemedText>
            <MaterialCommunityIcons
              name={showPasswordForm ? "chevron-up" : "chevron-down"}
              size={14}
              color={showPasswordForm ? colors.primary : colors.textSecondary}
            />
          </Pressable>

          {/* ── Email confirmation pill ── */}
          {!account.email_confirmed_at ? (
            <>
              <Pressable
                onPress={handleConfirmEmail}
                disabled={pillDisabled || confirmingEmail}
                style={({ hovered }) => [
                  styles.pill,
                  {
                    borderColor: colors.border,
                    backgroundColor: hovered && !pillDisabled ? colors.backgroundSecondary : "transparent",
                  },
                ]}
              >
                <MaterialCommunityIcons name="email-check-outline" size={14} color={colors.textSecondary} />
                <ThemedText style={[styles.pillText, { color: colors.text }]}>
                  {confirmingEmail ? "Confirming..." : "Confirm Email"}
                </ThemedText>
              </Pressable>
              <Pressable
                onPress={handleResendConfirmation}
                disabled={pillDisabled || confirmingEmail}
                style={({ hovered }) => [
                  styles.pill,
                  {
                    borderColor: colors.border,
                    backgroundColor: hovered && !pillDisabled ? colors.backgroundSecondary : "transparent",
                  },
                ]}
              >
                <MaterialCommunityIcons name="email-sync-outline" size={14} color={colors.textSecondary} />
                <ThemedText style={[styles.pillText, { color: colors.text }]}>
                  {confirmingEmail ? "Sending..." : "Resend Confirmation"}
                </ThemedText>
              </Pressable>
            </>
          ) : (
            <View style={[styles.pill, { borderColor: colors.emerald?.border || colors.border }]}>
              <MaterialCommunityIcons
                name="email-check-outline"
                size={14}
                color={colors.emerald?.text || colors.primary}
              />
              <ThemedText style={[styles.pillText, { color: colors.emerald?.text || colors.primary }]}>
                Email Confirmed
              </ThemedText>
            </View>
          )}
        </View>
      )}

      {/* ── Password form reveal ── */}
      {canUpdate && showPasswordForm && editing && (
        <View style={[styles.passwordSection, { borderColor: colors.border }]}>
          {/* Password Input */}
          <View style={styles.passwordInputContainer}>
            <TextInput
              placeholder="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              placeholderTextColor={colors.placeholder}
              style={[
                styles.passwordInput,
                {
                  color: colors.text,
                  backgroundColor: colors.backgroundSecondary,
                  borderColor: password
                    ? allRequirementsMet
                      ? colors.emerald?.border || colors.primary
                      : colors.red?.border || "red"
                    : colors.border,
                },
              ]}
            />
            <Pressable style={styles.eyeButton} onPress={() => setShowPassword(!showPassword)}>
              <MaterialIcons
                name={showPassword ? "visibility" : "visibility-off"}
                size={20}
                color={colors.textSecondary}
              />
            </Pressable>
          </View>

          {/* Confirm Password Input */}
          <View style={styles.passwordInputContainer}>
            <TextInput
              placeholder="Confirm Password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry={!showConfirmPassword}
              placeholderTextColor={colors.placeholder}
              style={[
                styles.passwordInput,
                {
                  color: colors.text,
                  backgroundColor: colors.backgroundSecondary,
                  borderColor: confirmPassword
                    ? passwordsMatch
                      ? colors.emerald?.border || colors.primary
                      : colors.red?.border || "red"
                    : colors.border,
                },
              ]}
            />
            <Pressable style={styles.eyeButton} onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
              <MaterialIcons
                name={showConfirmPassword ? "visibility" : "visibility-off"}
                size={20}
                color={colors.textSecondary}
              />
            </Pressable>
          </View>

          {confirmPassword.length > 0 && !passwordsMatch && (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <MaterialIcons name="error" size={16} color={colors.red?.text || "red"} />
              <ThemedText style={{ color: colors.red?.text || "red", fontSize: 13 }}>Passwords do not match</ThemedText>
            </View>
          )}

          {/* Requirements checklist */}
          <View
            style={[
              styles.requirementsBox,
              { backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
            ]}
          >
            <ThemedText style={{ fontSize: 13, fontWeight: "600", marginBottom: 8 }}>Password Requirements</ThemedText>
            <PasswordRequirementRow
              met={passwordChecks.minLength}
              text={`At least ${tenantRequirements.min_length} characters`}
              colors={colors}
            />
            {tenantRequirements.require_uppercase && (
              <PasswordRequirementRow met={passwordChecks.hasUppercase} text="One uppercase letter" colors={colors} />
            )}
            {tenantRequirements.require_lowercase && (
              <PasswordRequirementRow met={passwordChecks.hasLowercase} text="One lowercase letter" colors={colors} />
            )}
            {tenantRequirements.require_number && (
              <PasswordRequirementRow met={passwordChecks.hasNumber} text="One number" colors={colors} />
            )}
            {tenantRequirements.require_special && (
              <PasswordRequirementRow
                met={passwordChecks.hasSpecial}
                text="One special character (!@#$%^&*)"
                colors={colors}
              />
            )}
          </View>

          {/* Save Password button */}
          <Pressable
            onPress={handleSavePassword}
            disabled={!canSavePassword}
            style={({ hovered }) => [
              styles.saveButton,
              {
                backgroundColor: canSavePassword
                  ? hovered
                    ? colors.primary + "DD"
                    : colors.primary
                  : colors.backgroundSecondary,
                borderColor: canSavePassword ? colors.primary : colors.border,
              },
            ]}
          >
            {savingPassword ? (
              <ActivityIndicator size={14} color="white" />
            ) : (
              <MaterialCommunityIcons
                name="content-save"
                size={16}
                color={canSavePassword ? "white" : colors.textSecondary}
              />
            )}
            <ThemedText style={[styles.saveButtonText, { color: canSavePassword ? "white" : colors.textSecondary }]}>
              {savingPassword ? "Saving..." : "Save Password"}
            </ThemedText>
          </Pressable>
        </View>
      )}
    </PreviewSection>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  cell: {
    flex: 1,
    minWidth: 140,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 10,
    gap: 6,
  },
  cellHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  cellHeaderText: {
    fontSize: 9,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "600",
  },
  secondaryText: {
    fontSize: 11,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    marginLeft: 4,
  },
  statusText: {
    fontSize: 9,
    fontWeight: "700",
    letterSpacing: 0.3,
  },
  pillRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  pillRowDisabled: {
    opacity: 0.4,
    pointerEvents: "none",
  },
  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
  },
  pillText: {
    fontSize: 11,
    fontWeight: "600",
  },
  passwordSection: {
    gap: 10,
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  passwordInputContainer: {
    position: "relative",
  },
  passwordInput: {
    fontSize: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    paddingRight: 44,
    borderRadius: 8,
    borderWidth: 1,
    // @ts-ignore web-only
    outlineStyle: "none",
  },
  eyeButton: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: "center",
  },
  requirementsBox: {
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  requirementRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: "600",
  },
});
